import { REMOTE_TYPES, TRAIT_NAMES } from "../../constants/devices";

// Rules shared by the IR remote pages (app/screens/HashhRemote/* and
// app/screens/devices/Remote*.js).

// /remotes and /remote read the IR blaster from the location state
// ({ data: device } and { data: device, remote }). The add-remote steps pass
// on what was picked so far, and steps: how many of them are open, so saving
// can go back to Universal Remotes as the app's popToTop does.
export const REMOTE_PATHS = Object.freeze({
  LIST: "/remotes",
  VIEW: "/remote",
  UNIVERSAL: "/universal-remotes",
  ADD_BLASTER: "/universal-remotes/add/blaster",
  ADD_CATEGORY: "/universal-remotes/add/category",
  ADD_BRAND: "/universal-remotes/add/brand",
  ADD_TEST: "/universal-remotes/add/test",
});

// IR command for one key, as the app's RemoteLayout and RemoteLayoutView
// controlAPI build it.
export const blastPayload = (irbmId, code) => ({
  on: [irbmId],
  off: [],
  irbm: { [irbmId]: { action: "bst", data: code } },
});

// The control API finds a blaster by applianceId; the IRDB routes find it by
// deviceId. The API registers an IR blaster with both the same.
export const blasterIdOf = (blaster) => blaster.applianceId ?? blaster.deviceId;

// IR blasters: the devices that hold remotes, as the app's RemoteListingScreen
// and SelectIRBM pick them.
export const blastersOf = (devices) => (devices ?? []).filter((device) => device?.traits?.includes(TRAIT_NAMES.REMOTE));

// The app shows each remoteId once.
export const uniqueRemotes = (remotes) => {
  const seen = new Set();
  return (remotes ?? []).filter((remote) => {
    if (!remote || seen.has(remote.remoteId)) return false;
    seen.add(remote.remoteId);
    return true;
  });
};

// Every remote in the home with the blaster it's learned on, each remoteId
// once (the app's RemoteListingScreen).
export const remotesIn = (blasters) => {
  const seen = new Set();
  const list = [];
  blasters.forEach((blaster) =>
    uniqueRemotes(blaster.remoteDevices).forEach((remote) => {
      if (seen.has(remote.remoteId)) return;
      seen.add(remote.remoteId);
      list.push({ blaster, remote });
    })
  );
  return list;
};

export const placeOf = (device) => [device?.floor, device?.room].filter(Boolean).join(" | ");

export const remoteNameOf = (remote) => remote?.name || `${remote?.deviceType ?? ""} remote`.trim();

// The app leaves out IRDB category 11 and opens only the categories it has a
// remote layout for; the others show dimmed (app/screens/HashhRemote/AddRemoteModal.js).
export const HIDDEN_CATEGORY_ID = 11;
export const canAddCategory = (category) => Object.values(REMOTE_TYPES).includes(category?.name_en);

// Brands load 15 at a time, as in the app's ChooseBrand.
export const BRANDS_PER_PAGE = 15;

// The IRDB lists are arrays. When its database fails the API still answers
// 200, with the error instead, so anything else counts as a failure (null).
export const asList = (response) => (Array.isArray(response) ? response : null);

// Adding, renaming and deleting answer Mongo's update result { n, nModified, ok }:
// n counts the home and nModified whether the blaster's remotes changed, so 0
// means no blaster or remote matched.
export const changedNothing = (result) => result?.nModified === 0;
